import React, {useCallback, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import api from '../../../../api'
import ComponentButton, {getLabel} from '..'
import ComponentTooltip from '../../ComponentTooltip'
import {getErrorToast, getSuccessToast} from '../../../helpers/toasts'
import {CryptoProService, CryptoCert} from '../../../helpers/cryptopro/cryptoProService'
import {TComponentButtonEgiszSign} from '../_types'

type TDraft = {
    id: number
    status: string
    hash: string
    doctor_signed: boolean
    director_signed: boolean
    is_current_user_doctor: boolean
}

export const EGISZ_STATUS_LABELS: Record<string, string> = {
    pending_doctor: 'Ожидает подписи врача',
    pending_director: 'Ожидает подписи директора',
    signed: 'Подписан',
    submitted: 'Отправлен в ЕГИСЗ',
    error: 'Ошибка отправки',
}

const cryptoService = new CryptoProService()

const ComponentButtonEgiszSign: React.FC<TComponentButtonEgiszSign> = ({settings, defaultLabel = 'title'}) => {
    const [draft, setDraft] = useState<TDraft | null>(null)
    const [show, setShow] = useState(false)
    const [certs, setCerts] = useState<CryptoCert[]>([])
    const [isLoading, setIsLoading] = useState(false)

    const loadDraft = useCallback(async () => {
        try {
            const response = await api('egiszDrafts', 'get', {visit_id: settings.data.row_id})
            setDraft(response.data ?? null)
        } catch {
            setDraft(null)
        }
    }, [settings.data.row_id])

    useEffect(() => {
        loadDraft()
    }, [loadDraft])

    const role: 'doctor' | 'director' | null = !draft
        ? null
        : !draft.doctor_signed
            ? (draft.is_current_user_doctor ? 'doctor' : null)
            : !draft.director_signed
                ? 'director'
                : null

    const openModal = useCallback(async () => {
        if (!role) return
        setShow(true)
        setIsLoading(true)
        try {
            const list = await cryptoService.getCertificates()
            setCerts(list)
        } catch (error: any) {
            getErrorToast(error.message)
            setShow(false)
        } finally {
            setIsLoading(false)
        }
    }, [role])

    // TODO: подписание временно отключено — CryptoProService ещё не реализует
    // signHashDetached/exportCertificateBase64. Вернуться к этому позже.
    const handleSign = useCallback(async (_thumbprint: string) => {
        getErrorToast('Подписание временно недоступно')
    }, [])

    if (!role) return null

    return (
        <>
            <ComponentTooltip title={settings.title ?? ''} show={defaultLabel === 'icon' ? undefined : false}>
                <button
                    className={`componentButton ${settings.background}`}
                    type="button"
                    onClick={openModal}
                >
                    {getLabel(defaultLabel, {
                        ...settings,
                        title: role === 'doctor' ? 'Подписать (врач)' : 'Подписать (директор)',
                    })}
                </button>
            </ComponentTooltip>

            <Modal show={show} onHide={() => setShow(false)} size="sm" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Выберите сертификат</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoading ? (
                        <p>Загрузка...</p>
                    ) : certs.length === 0 ? (
                        <p>Сертификаты не найдены. Проверьте плагин КриптоПро.</p>
                    ) : (
                        <div className="d-flex flex-column gap-2">
                            {certs.map((cert) => (
                                <ComponentButton
                                    key={cert.thumbprint}
                                    type="custom"
                                    settings={{title: cert.subject ?? cert.thumbprint, background: 'dark', icon: ''}}
                                    customHandler={() => handleSign(cert.thumbprint)}
                                />
                            ))}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    )
}

export default ComponentButtonEgiszSign